import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CalendarDays, QrCode, Receipt, FileSignature, Brain, FileSpreadsheet, ArrowUpRight, Check, Calendar, Sun, Moon, BookOpen, ArrowRight } from "lucide-react";
import axios from "axios";
import Pricing from "./_Pricing";
import FeatureCarousel from "./_FeatureCarousel";
import HeroCarousel from "./_HeroCarousel";
import HowTheyUseIt from "./_HowTheyUseIt";
import { LogoMark } from "../components/Logo";
import { DemoRequestModal } from "../components/DemoRequestModal";
import { BackToTop, Counter } from "../components/Interactions";
import { useTheme } from "../lib/theme";
import { useAuth } from "../lib/auth";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e519c322-4337-4cae-976f-224e1ee38602/images/0f503b3e884fb6c778b602080603c8a60c7690ae71cb1ba0cf4dbe79917de5f7.png";
const PAT_IMG = "https://static.prod-images.emergentagent.com/jobs/e519c322-4337-4cae-976f-224e1ee38602/images/5a49a4e52276e0d9f16a9354a69e1e5cb26b8e2b25fcb1864fe3251de6785beb.png";
const PORTRAIT_1 = "https://images.unsplash.com/photo-1610387694365-19fafcc86d86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY0MTM1MTV8MA&ixlib=rb-4.1.0&q=85";
const PORTRAIT_2 = "https://images.pexels.com/photos/28442317/pexels-photo-28442317.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function Nav({ onDemo }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#EDE5D8]" data-testid="landing-nav">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-28 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-home-link" aria-label="Tool Badge home">
          <LogoMark size={88} pulse />
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm">
          <a href="#features" className="hover:text-[#FF7A29] transition-colors">Funzioni</a>
          <a href="#pricing" className="hover:text-[#FF7A29] transition-colors">Prezzi</a>
          <a href="#testimonials" className="hover:text-[#FF7A29] transition-colors">Clienti</a>
          <Link to="/blog" className="hover:text-[#FF7A29] transition-colors">Blog</Link>
          <button onClick={onDemo} data-testid="nav-demo-btn" className="hover:text-[#FF7A29] font-semibold transition-colors">Richiedi demo</button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label={isDark ? "Passa a tema chiaro" : "Passa a tema scuro"}
            className="w-10 h-10 rounded-full border border-[#EDE5D8] flex items-center justify-center hover:bg-[#FFF0E3] hover:border-[#FF7A29] hover:text-[#FF7A29] transition-all"
          >
            {isDark ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>}
          </button>
          <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn" className="rounded-full hover:bg-[#FFF0E3]">Accedi</Button></Link>
          <Link to="/register"><Button data-testid="nav-register-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] text-white btn-glow shadow-md shadow-orange-200">Inizia ora</Button></Link>
        </div>
      </div>
    </nav>
  );
}

function Hero({ onDemo }) {
  return (
    <section className="relative overflow-hidden" data-testid="hero">
      <div className="orb orb-orange" style={{width: 600, height: 600, top: -200, right: -100}}/>
      <div className="orb orb-black" style={{width: 400, height: 400, bottom: -200, left: -100, opacity: 0.15}}/>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 reveal reveal-1">
          <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-6">
            <span className="w-2 h-2 bg-[#FF7A29] rounded-full animate-pulse"/> Gestione HR · Made in Italy · 2026
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[92px] leading-[0.95] tracking-tighter">
            L'HR della tua azienda,<br/><span className="text-[#FF7A29]">finalmente smart.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#555] max-w-xl leading-relaxed">
            Calendario intelligente, presenze geolocalizzate via QR, rimborsi spese, firma digitale e analisi AI.
            Una piattaforma sola, zero fogli Excel, zero email di approvazione.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register"><Button data-testid="hero-cta-primary" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] text-white h-14 px-8 text-base btn-glow shadow-lg shadow-orange-200/50">Prova gratis 14 giorni <ArrowUpRight className="ml-1 w-4 h-4"/></Button></Link>
            <Button onClick={onDemo} variant="outline" data-testid="hero-cta-demo" className="rounded-full h-14 px-8 text-base border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors">
              <Calendar className="mr-2 w-4 h-4"/> Richiedi demo live
            </Button>
          </div>
          <div className="mt-10 flex items-center gap-6 text-xs text-[#757575]">
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-[#FF7A29]"/> Conforme GDPR</span>
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-[#FF7A29]"/> Export Zucchetti / TeamSystem / Inaz</span>
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-[#FF7A29]"/> Senza carta</span>
          </div>
        </div>
        <div className="md:col-span-5 reveal reveal-2">
          <div className="relative float">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#FF7A29]/30 to-transparent rounded-[32px] blur-2xl"/>
            <div className="relative rounded-[28px] overflow-hidden shadow-2xl shadow-orange-200/40 ring-1 ring-black/5">
              <img src={HERO_IMG} alt="Tool Badge office" className="w-full h-[420px] md:h-[520px] object-cover grayscale contrast-125" />
              <div className="absolute left-4 top-4 bg-[#FF7A29] text-white px-4 py-2 text-xs uppercase tracking-widest rounded-full font-semibold">Dashboard AI</div>
              <div className="absolute right-4 bottom-4 bg-white rounded-2xl px-5 py-3 shadow-xl">
                <div className="text-2xl font-display">+42%</div>
                <div className="text-xs text-[#757575]">Produttività HR</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetricsBand() {
  const items = [
    { end: 42, prefix: "+", suffix: "%", decimals: 0, k: "Produttività HR" },
    { end: 12, prefix: "", suffix: " min", decimals: 0, k: "Setup iniziale" },
    { end: 350, prefix: "", suffix: "+", decimals: 0, k: "PMI italiane" },
    { end: 99.9, prefix: "", suffix: "%", decimals: 1, k: "Uptime SLA" },
  ];
  return (
    <div className="bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="metrics-band">
      <div className="orb orb-orange" style={{width: 500, height: 500, top: -250, left: "30%", opacity: 0.25}}/>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
        {items.map((m, i) => (
          <div key={i} className="p-8 md:p-10">
            <Counter
              end={m.end}
              prefix={m.prefix}
              suffix={m.suffix}
              decimals={m.decimals}
              className="font-display text-5xl md:text-6xl tracking-tight bg-gradient-to-br from-white to-[#FF7A29] bg-clip-text text-transparent"
            />
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-3">{m.k}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Registrati in 60 secondi", d: "Crea l'account aziendale senza carta di credito. Onboarding automatico dei primi dipendenti." },
    { n: "02", t: "Configura sedi e QR", d: "Genera i QR code geolocalizzati delle tue sedi. Raggio e regole personalizzabili." },
    { n: "03", t: "Lascia lavorare l'AI", d: "GPT-5.2 analizza le presenze e produce report operativi. Export diretto verso il gestionale paghe." },
  ];
  return (
    <section className="bg-[#FFFAF5]" data-testid="how-it-works">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-5">Come funziona</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Operativo in tre mosse.<br/><span className="text-[#757575]">Nessun formazione richiesta.</span></h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className={`card-smart p-8 md:p-10 relative ${i === 1 ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`} data-testid={`how-step-${i}`}>
              <div className={`font-display text-7xl mb-6 ${i === 1 ? "text-[#FF7A29]" : "text-[#FF7A29]/90"}`}>{s.n}</div>
              <h3 className="font-display text-2xl mb-3 tracking-tight">{s.t}</h3>
              <p className={`text-sm leading-relaxed ${i === 1 ? "text-white/75" : "text-[#555]"}`}>{s.d}</p>
              {i < 2 && <ArrowUpRight className={`absolute top-8 right-8 w-5 h-5 ${i === 1 ? "text-white/40" : "text-[#FF7A29]/60"}`}/>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LogosStrip() {
  return (
    <div className="bg-white border-y border-[#EDE5D8] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex items-center gap-12 text-sm text-[#757575]">
        <span className="shrink-0 uppercase text-xs tracking-widest font-semibold">Integrato con</span>
        <div className="flex items-center gap-12 flex-wrap">
          {["Zucchetti", "TeamSystem", "Inaz", "Google Calendar", "Stripe", "Fatture in Cloud"].map((n) => (
            <span key={n} className="font-display text-xl tracking-tight text-[#999] hover:text-[#FF7A29] transition-colors">{n}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Features() {
  const items = [
    { icon: CalendarDays, title: "Calendario Intelligente", desc: "Eventi, ricorrenze, promemoria automatici. Sincronizzato in tempo reale con il team.", span: "md:col-span-6 md:row-span-2", tone: "light" },
    { icon: QrCode, title: "QR + Geolocalizzazione", desc: "Timbratura con QR e validazione GPS entro il raggio consentito. Niente più badge smarriti.", span: "md:col-span-6", tone: "dark" },
    { icon: Receipt, title: "Rimborsi Spese", desc: "Upload scontrini, approvazione in un click, export diretto in paghe.", span: "md:col-span-3", tone: "light" },
    { icon: FileSignature, title: "Firma Digitale", desc: "Canvas touch + hash SHA-256 crittografico. Valore legale.", span: "md:col-span-3", tone: "light" },
    { icon: FileSpreadsheet, title: "Export Paghe", desc: "CSV compatibili Zucchetti · TeamSystem · Inaz, senza riformattazioni.", span: "md:col-span-6", tone: "light" },
    { icon: Brain, title: "AI Analisi Presenze", desc: "GPT-5.2 rileva anomalie, pattern e produce raccomandazioni in italiano.", span: "md:col-span-6", tone: "accent" },
  ];
  return (
    <section id="features" className="bg-white" data-testid="features">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-6">
            <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-5">Tutto in una piattaforma</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">
              <Counter end={6} suffix="" className="text-[#FF7A29]"/> moduli.<br/>Un'unica fonte di verità.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 text-[#555]">
            Progettata per sostituire fogli Excel, badge fisici ed email di approvazione. Tool Badge centralizza presenze, spese, documenti e analisi in un'unica dashboard.
          </div>
        </div>
        <div className="grid md:grid-cols-12 gap-5">
          {items.map((it, i) => {
            const Icon = it.icon;
            const dark = it.tone === "dark";
            const accent = it.tone === "accent";
            return (
              <div
                key={i}
                data-testid={`feature-card-${i}`}
                className={`card-smart ${it.span} p-8 md:p-10 min-h-[240px] relative overflow-hidden group ${dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : accent ? "bg-[#FF7A29] text-white border-[#FF7A29]" : ""}`}
              >
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 group-hover:rotate-3 ${dark ? "bg-[#FF7A29]" : accent ? "bg-white text-[#FF7A29]" : "bg-[#FFF0E3] text-[#FF7A29]"}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3">{it.title}</h3>
                <p className={`text-sm ${dark || accent ? "text-white/80" : "text-[#555]"} leading-relaxed max-w-md`}>{it.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="testimonials" className="bg-[#FFFAF5]" data-testid="testimonials">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-8">
        {[
          { q: "Abbiamo eliminato il cartellino cartaceo in due settimane. L'export per Zucchetti funziona al primo colpo.", name: "Giulia Romano", role: "HR Manager · Logistica Nord-Est", img: PORTRAIT_1 },
          { q: "Il calendario intelligente ci ha fatto risparmiare ore di email. Le firme digitali hanno valore legale certificato.", name: "Marco De Angelis", role: "CEO · Studio legale Milano", img: PORTRAIT_2 },
        ].map((t, i) => (
          <figure key={i} className="card-smart p-8 md:p-10 relative" data-testid={`testimonial-${i}`}>
            <div className="text-[#FF7A29] font-display text-6xl leading-none mb-2">"</div>
            <blockquote className="font-display text-2xl md:text-3xl leading-tight tracking-tight -mt-4">
              {t.q}
            </blockquote>
            <figcaption className="mt-8 flex items-center gap-4">
              <img src={t.img} alt={t.name} className="w-14 h-14 object-cover rounded-full" />
              <div>
                <div className="font-semibold">{t.name}</div>
                <div className="text-xs text-[#757575] uppercase tracking-widest">{t.role}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function BlogTeaser() {
  const [posts, setPosts] = React.useState([]);
  React.useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/blog/posts?limit=3`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);
  if (!posts || posts.length === 0) return null;
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };
  return (
    <section id="blog" className="bg-white py-20" data-testid="landing-blog-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-4">
              <BookOpen className="w-3.5 h-3.5"/> Blog & Guide
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">Idee e best practice per la gestione HR</h2>
            <p className="text-[#555] mt-3 max-w-2xl">Timbrature digitali, ferie online, compliance GDPR, smart working. Articoli brevi, pratici, scritti dal nostro team.</p>
          </div>
          <Link to="/blog" data-testid="blog-see-all" className="hidden md:inline-flex items-center gap-2 text-[#FF7A29] font-semibold hover:gap-3 transition-all">
            Vedi tutti gli articoli <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`landing-blog-card-${p.slug}`} className="group block">
              <article className="card-smart overflow-hidden h-full flex flex-col hover:shadow-xl hover:shadow-orange-200/40 transition-all">
                {p.cover_image_url ? (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  </div>
                ) : (
                  <div className="aspect-[16/10] bg-gradient-to-br from-[#FF7A29] to-[#E85D00] flex items-center justify-center">
                    <BookOpen className="w-14 h-14 text-white/80"/>
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-[#FF7A29] transition-colors">{p.title}</h3>
                  <p className="text-sm text-[#555] flex-1">{p.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-[#757575] mt-4 pt-3 border-t border-[#EDE5D8]">
                    <span>{fmt(p.published_at)}</span>
                    <ArrowRight className="w-4 h-4 text-[#FF7A29] group-hover:translate-x-1 transition-transform"/>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>
        <div className="md:hidden text-center mt-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#FF7A29] font-semibold">Vedi tutti gli articoli <ArrowRight className="w-4 h-4"/></Link>
        </div>
      </div>
    </section>
  );
}


function Footer() {  return (
    <footer className="bg-[#0A0A0A] text-white relative overflow-hidden">
      <div className="orb orb-orange" style={{width: 600, height: 600, bottom: -400, right: -200, opacity: 0.15}}/>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center mb-6">
            <LogoMark size={56}/>
          </div>
          <p className="text-sm text-white/60 leading-relaxed">La piattaforma HR all-in-one per PMI italiane. Presenze, spese, documenti, AI.</p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FF7A29] mb-4 font-semibold">Prodotto</div>
          <ul className="space-y-2.5 text-sm text-white/70"><li>Calendario</li><li>Presenze QR</li><li>Rimborsi</li><li>AI Analisi</li></ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FF7A29] mb-4 font-semibold">Azienda</div>
          <ul className="space-y-2.5 text-sm text-white/70"><li>Chi siamo</li><li>Clienti</li><li>Contatti</li></ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FF7A29] mb-4 font-semibold">Legale</div>
          <ul className="space-y-2.5 text-sm text-white/70"><li><Link to="/privacy" className="hover:text-[#FF7A29]">Privacy · GDPR</Link></li><li><Link to="/terms" className="hover:text-[#FF7A29]">Termini</Link></li><li>Cookie tecnici</li></ul>
        </div>
      </div>
      <div className="relative border-t border-white/10 py-5 text-xs text-white/40 text-center">© 2026 Tool Badge · Tutti i diritti riservati</div>
    </footer>
  );
}

function DemoBanner({ onDemo }) {
  return (
    <section className="bg-[#FFFAF5]" data-testid="demo-banner">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="relative overflow-hidden rounded-[32px] bg-[#0A0A0A] text-white p-10 md:p-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="orb orb-orange" style={{width: 400, height: 400, top: -200, right: -50, opacity: 0.4}}/>
          <div className="relative md:col-span-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF7A29] mb-4 font-semibold">
              <Calendar className="w-3.5 h-3.5"/> Demo guidata · 20 min · Gratis
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Parliamone.<br/><span className="text-[#FF7A29]">Un consulente HR ti aspetta.</span>
            </h2>
            <p className="text-sm md:text-base text-white/75 mt-4 max-w-2xl">
              Ti mostreremo come Tool Badge si adatta alla tua struttura: settore, numero di sedi, integrazioni paghe. Nessun impegno.
            </p>
          </div>
          <div className="relative md:col-span-4 flex md:justify-end">
            <Button onClick={onDemo} data-testid="banner-demo-btn" className="rounded-full bg-[#FF7A29] text-white hover:bg-white hover:text-[#0A0A0A] h-14 px-8 text-base btn-glow shadow-lg shadow-orange-500/30">
              <Calendar className="mr-2 w-5 h-5"/> Richiedi la demo
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);
  const nav = useNavigate();
  const { user } = useAuth();

  // PWA installata → salta la home commerciale e vai diretto al login (o all'app se già loggato).
  // Rileva display-mode standalone (Android/Desktop) o standalone iOS (Safari legacy).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.matchMedia?.("(display-mode: minimal-ui)")?.matches ||
      window.navigator?.standalone === true;
    if (!isStandalone) return;
    // Se già loggato → vai all'app, altrimenti login
    if (user) {
      const target = user.role === "employee" ? "/portal" : "/app";
      nav(target, { replace: true });
    } else {
      nav("/login", { replace: true });
    }
  }, [user, nav]);

  return (
    <div className="min-h-screen bg-[#FFFAF5]">
      <Nav onDemo={openDemo} />
      <HeroCarousel onDemo={openDemo} />
      <MetricsBand />
      <HowTheyUseIt />
      <LogosStrip />
      <FeatureCarousel />
      <DemoBanner onDemo={openDemo} />
      <Features />
      <HowItWorks />
      <Pricing onDemo={openDemo} />
      <Testimonials />
      <BlogTeaser />
      <Footer />
      <DemoRequestModal open={demoOpen} onOpenChange={setDemoOpen} source="demo"/>
      <BackToTop />
    </div>
  );
}
